import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { API_CONFIG } from '../../../utils/apiConfig';

const ProductReturns = () => {
  const [returns, setReturns] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const API_BASE_URL = API_CONFIG.BASE_URL;

  // Get Firebase ID token for API calls
  const getAuthToken = useCallback(async () => {
    if (!currentUser) {
      throw new Error('No authenticated user');
    }
    return await currentUser.getIdToken(true);
  }, [currentUser]);

  const fetchReturns = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const token = await getAuthToken();
      
      // Fetch credit notes as product returns
      const response = await axios.get(`${API_BASE_URL}/api/creditnotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Process credit notes as product returns
      let creditNotes = [];
      if (Array.isArray(response.data)) {
        creditNotes = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        creditNotes = response.data.data;
      } else if (response.data && response.data.creditNotes) {
        creditNotes = response.data.creditNotes;
      }

      // Transform credit notes to product returns format
      const productReturns = [];
      creditNotes.forEach(creditNote => {
        if (creditNote.items && Array.isArray(creditNote.items)) {
          creditNote.items.forEach((item, index) => {
            if (item.returnQty > 0) { // Only include items that were actually returned
              productReturns.push({
                id: `${creditNote.id || creditNote._id}-${index}`,
                itemCode: item.itemCode || item.code || '',
                itemName: item.name || item.description || '',
                quantity: item.returnQty || 0,
                unitPrice: item.rate || item.price || 0,
                totalAmount: (item.returnQty || 0) * (item.rate || 0),
                gstAmount: ((item.returnQty || 0) * (item.rate || 0) * 18) / 100, // Assuming 18% GST
                returnType: 'credit-note',
                creditNoteNumber: creditNote.invoiceNumber || creditNote.creditNoteNumber || 'N/A',
                returnDate: creditNote.date || creditNote.createdAt || new Date(),
                formattedReturnDate: creditNote.date ? new Date(creditNote.date).toLocaleDateString('en-IN') : 
                                   creditNote.createdAt ? new Date(creditNote.createdAt).toLocaleDateString('en-IN') : 
                                   new Date().toLocaleDateString('en-IN'),
                reason: creditNote.reason || 'Customer return',
                processedBy: creditNote.createdBy || creditNote.lastUpdatedBy || 'Unknown'
              });
            }
          });
        }
      });

      setReturns(productReturns);
      
    } catch (err) {
      console.error('Error fetching returns:', err);
      if (err.response?.status === 404 || err.message?.includes('404') || err.message?.includes('Not Found')) {
        setError('Credit notes endpoint not available on server. Returns features may be limited.');
        setReturns([]); // Set empty array for graceful degradation
      } else {
        setError(err.response?.data?.message || 'Failed to fetch returns');
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser, getAuthToken, API_BASE_URL]);

  const fetchSummary = useCallback(async () => {
    if (!currentUser) return;
    
    try {
      const token = await getAuthToken();
      
      // Fetch credit notes to calculate summary
      const response = await axios.get(`${API_BASE_URL}/api/creditnotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Process credit notes data for summary
      let creditNotes = [];
      if (Array.isArray(response.data)) {
        creditNotes = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        creditNotes = response.data.data;
      } else if (response.data && response.data.creditNotes) {
        creditNotes = response.data.creditNotes;
      }

      // Calculate summary from credit notes
      let totalReturns = 0;
      let totalQuantity = 0;
      let totalValue = 0;
      const returnsByType = {};
      const recentReturns = [];

      creditNotes.forEach(creditNote => {
        if (creditNote.items && Array.isArray(creditNote.items)) {
          creditNote.items.forEach((item, index) => {
            if (item.returnQty > 0) {
              totalReturns++;
              totalQuantity += item.returnQty || 0;
              const itemValue = (item.returnQty || 0) * (item.rate || 0);
              totalValue += itemValue;

              // Group by return type
              const returnType = 'credit-note';
              if (!returnsByType[returnType]) {
                returnsByType[returnType] = { count: 0, quantity: 0, value: 0 };
              }
              returnsByType[returnType].count++;
              returnsByType[returnType].quantity += item.returnQty || 0;
              returnsByType[returnType].value += itemValue;

              // Add to recent returns (limit to 10)
              if (recentReturns.length < 10) {
                recentReturns.push({
                  itemName: item.name || item.description || '',
                  itemCode: item.itemCode || item.code || '',
                  quantity: item.returnQty || 0,
                  unitPrice: item.rate || 0,
                  returnDate: creditNote.date || creditNote.createdAt || new Date(),
                  returnType: returnType
                });
              }
            }
          });
        }
      });

      const calculatedSummary = {
        totalReturns,
        totalQuantity,
        totalValue,
        returnsByType,
        recentReturns: recentReturns.slice(0, 10)
      };

      setSummary(calculatedSummary);
      
    } catch (err) {
      console.error('Error fetching returns summary:', err);
      if (err.response?.status === 404 || err.message?.includes('404') || err.message?.includes('Not Found')) {
        console.warn('Credit notes endpoint not available on server');
        setSummary(null); // Set null for graceful degradation
      }
    }
  }, [currentUser, getAuthToken, API_BASE_URL]);

  useEffect(() => {
    if (currentUser) {
      fetchReturns();
      fetchSummary();
    }
  }, [fetchReturns, fetchSummary, currentUser]);

  if (loading) {
    return <div className="loading">Loading returns data...</div>;
  }

  return (
    <div className="product-returns-panel">
      <button className="bck-button" onClick={() => navigate(-1)}>← Back</button>
      <h2>📦 Product Returns Management</h2>

      {error && <div className="error-message">{error}</div>}

      {/* Summary Section */}
      {summary && (
        <div className="returns-summary" style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h3>📊 Returns Summary</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div className="summary-card">
              <h4>Total Returns</h4>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#dc3545' }}>{summary.totalReturns}</p>
            </div>
            <div className="summary-card">
              <h4>Total Quantity</h4>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fd7e14' }}>{summary.totalQuantity}</p>
            </div>
            <div className="summary-card">
              <h4>Total Value</h4>
              <p style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>₹{summary.totalValue?.toFixed(2)}</p>
            </div>
          </div>
          
          {/* Returns by Type */}
          {summary.returnsByType && Object.keys(summary.returnsByType).length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h4>Returns by Type</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem' }}>
                {Object.entries(summary.returnsByType).map(([type, data]) => (
                  <div key={type} style={{ padding: '0.5rem', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                    <strong>{type}</strong>
                    <div>Count: {data.count}</div>
                    <div>Qty: {data.quantity}</div>
                    <div>Value: ₹{data.value?.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Returns Table */}
      <div className="returns-table-section">
        <h3>🔄 All Product Returns (from Credit Notes)</h3>
        {returns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>No product returns found.</p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              Product returns are created when customers return items via credit notes.
            </p>
          </div>
        ) : (
          <table border="1" cellPadding="8" style={{ fontSize: '12px', width: '100%' }}>
            <thead>
              <tr>
                <th>Item Code</th>
                <th>Item Name</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total Amount</th>
                <th>GST Amount</th>
                <th>Return Type</th>
                <th>Credit Note #</th>
                <th>Return Date</th>
                <th>Reason</th>
                <th>Processed By</th>
              </tr>
            </thead>
            <tbody>
              {returns.map((ret) => (
                <tr key={ret.id} style={{ 
                  backgroundColor: ret.returnType === 'credit-note' ? '#d4edda' : '#fff3cd'
                }}>
                  <td>{ret.itemCode}</td>
                  <td>{ret.itemName}</td>
                  <td style={{ fontWeight: 'bold', color: '#28a745' }}>{ret.quantity}</td>
                  <td>₹{ret.unitPrice?.toFixed(2)}</td>
                  <td>₹{ret.totalAmount?.toFixed(2)}</td>
                  <td>₹{ret.gstAmount?.toFixed(2)}</td>
                  <td style={{ 
                    color: ret.returnType === 'credit-note' ? '#155724' : '#856404',
                    fontWeight: 'bold'
                  }}>
                    {ret.returnType === 'credit-note' ? 'Credit Note' : ret.returnType}
                  </td>
                  <td>{ret.creditNoteNumber || 'N/A'}</td>
                  <td>{ret.formattedReturnDate}</td>
                  <td style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                    {ret.reason || 'Customer return'}
                  </td>
                  <td>{ret.processedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent Returns Section */}
      {summary?.recentReturns && summary.recentReturns.length > 0 && (
        <div className="recent-returns" style={{ marginTop: '2rem' }}>
          <h3>🕒 Recent Returns (Last 10)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
            {summary.recentReturns.map((ret, index) => (
              <div key={index} style={{ 
                padding: '1rem', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6' 
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <strong>{ret.itemName}</strong>
                  <span style={{ 
                    backgroundColor: ret.returnType === 'credit-note' ? '#d4edda' : '#fff3cd',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem'
                  }}>
                    {ret.returnType === 'credit-note' ? 'Credit Note' : ret.returnType}
                  </span>
                </div>
                <div>Item Code: {ret.itemCode}</div>
                <div>Quantity: <strong style={{ color: '#28a745' }}>{ret.quantity}</strong></div>
                <div>Value: ₹{((ret.quantity || 0) * (ret.unitPrice || 0)).toFixed(2)}</div>
                <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '0.5rem' }}>
                  {ret.returnDate ? new Date(ret.returnDate).toLocaleDateString() : 'N/A'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default ProductReturns;
